import React from 'react'

import { ReactComponent as SortIcon } from 'src/assets/svg/arrow-3.svg'
import { ReactComponent as MastercardIcon } from 'src/assets/svg/mastercard_symbol.svg.svg'
import { ReactComponent as DownloadIcon } from 'src/assets/svg/Settings-download.svg'
import { ReactComponent as VisaIcon } from 'src/assets/svg/visa_inc_logo.svg.svg'
import { useGetInvoicesQuery } from 'src/reactQuery/useGetInvoicesQuery'
import { Invoice } from 'src/services/subscriptionApi/interfaces/subscriptionApi.interface'

import { Skeleton } from 'src/components/Skeleton'

import styles from './billingHistory.module.scss'
import { BillingTransaction } from './types'

// One skeleton cell per column, widths loosely tracking the real content
// (plan name is longest, status/method shortest).
const INVOICE_SKELETON_CELL_WIDTHS = [104, 64, 72, 56, 88, 40]
const INVOICE_SKELETON_COUNT = 4

const mapInvoiceToTransaction = (invoice: Invoice): BillingTransaction => {
  const date = new Date(invoice.created)
  const formattedDate = date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  return {
    amount: invoice.amount, // in cents
    cardLast4: '****',
    cardType: 'mastercard',
    currency: invoice.currency,
    date: formattedDate,
    id: invoice.id,
    invoicePdf: invoice.pdfUrl,
    method: 'Card',
    planName: 'Subscription',
    status: invoice.status === 'paid' ? 'paid' : 'failed',
  }
}

// Mirrors the Subscription page's formatAmount: amount is in cents.
const formatAmount = (amount: number, currency: string) => {
  return new Intl.NumberFormat('en-US', {
    currency: (currency || 'usd').toUpperCase(),
    style: 'currency',
  }).format(amount / 100)
}

const BillingHistory: React.FC = () => {
  const { data: invoicesData, isError, isLoading } = useGetInvoicesQuery()

  const transactions: BillingTransaction[] =
    invoicesData?.data?.invoices?.map(mapInvoiceToTransaction) || []

  const handleDownloadInvoice = (transaction: BillingTransaction) => {
    if (transaction.invoicePdf) {
      window.open(transaction.invoicePdf, '_blank')
    }
  }

  const getStatusBadge = (status: string) => {
    return (
      <span className={`${styles.statusBadge} ${styles[status]}`}>
        {status === 'paid' ? 'Paid' : 'Failed'}
      </span>
    )
  }

  const getCardIcon = (cardType: string) => {
    if (cardType === 'visa') {
      return <VisaIcon height={12} width={37} />
    }
    if (cardType === 'mastercard') {
      return <MastercardIcon height={22} width={35} />
    }
    return null
  }

  if (isLoading) {
    return (
      <div className={styles.billingHistory}>
        <div className={styles.header}>
          <h2 className={styles.title}>Billing History</h2>
          <p className={styles.subtitle}>See The Transaction You Made</p>
        </div>
        {/* The table header is static markup, not data — keep it up and
            skeleton only the cells, so the column widths are settled before
            the rows arrive. */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Plan Name</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Method</th>
                <th>
                  Date <SortIcon className={styles.sortIcon} />
                </th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: INVOICE_SKELETON_COUNT }).map(
                (_, rowIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={rowIndex}>
                    {INVOICE_SKELETON_CELL_WIDTHS.map((width, cellIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={cellIndex}>
                        <Skeleton height={14} radius={4} width={width} />
                      </td>
                    ))}
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={styles.billingHistory}>
        <div className={styles.header}>
          <h2 className={styles.title}>Billing History</h2>
          <p className={styles.subtitle}>See The Transaction You Made</p>
        </div>
        <div className={styles.errorState}>Failed to load billing history</div>
      </div>
    )
  }

  return (
    <div className={styles.billingHistory}>
      <div className={styles.header}>
        <h2 className={styles.title}>Billing History</h2>
        <p className={styles.subtitle}>See The Transaction You Made</p>
      </div>

      {transactions.length === 0 ? (
        <div className={styles.emptyState}>No billing history available</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Plan Name</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Method</th>
                <th>
                  Date <SortIcon className={styles.sortIcon} />
                </th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td>{transaction.planName}</td>
                  <td>{getStatusBadge(transaction.status)}</td>
                  <td>
                    {formatAmount(transaction.amount, transaction.currency)}
                  </td>
                  <td>
                    <div className={styles.methodCell}>
                      <div className={styles.cardIconContainer}>
                        {getCardIcon(transaction.cardType)}
                      </div>
                      {transaction.method} ({transaction.cardLast4})
                    </div>
                  </td>
                  <td>{transaction.date}</td>
                  <td>
                    <button
                      aria-label="Download invoice"
                      className={styles.downloadButton}
                      disabled={!transaction.invoicePdf}
                      onClick={() => handleDownloadInvoice(transaction)}
                    >
                      <DownloadIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BillingHistory
